import { useContext, useEffect, useRef, useState } from 'react';
import { Controller, FieldPath, UseFormReturn } from 'react-hook-form';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/Accordion';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { Flex } from 'components/layout';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import {
  LoadDateTimeInput,
  LoadSelectInput,
  LoadTextInput,
} from 'pages/LoadView/LoadInformation/Components';
import { LoadStopTypes, LoadStopsItemTypes, NormalizedLoad } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { TMS } from 'types/enums/Integrations';
import {
  LoadStopNestedSection,
  suggestionTouchesStopDetails,
} from 'utils/expandStopDetailsOnSuggestion';

import { LoadBuildingTextInput } from '../QuantumEdgeLoadForm';
import { quantumEdgeApptTypes } from './constants';

type StopFormProps = {
  stop: LoadStopTypes;
  formMethods: UseFormReturn<NormalizedLoad>;
  isCreateMode: boolean;
  stopIndex?: number;
  autoExpandAccordions?: boolean;
  originalSuggestionData?: NormalizedLoad;
};

const datetimeFieldOptions = {};

export default function StopForm({
  stop,
  isCreateMode,
  formMethods,
  stopIndex: providedStopIndex,
  autoExpandAccordions = false,
  originalSuggestionData,
}: StopFormProps): React.JSX.Element {
  const { tmsName, tenant } = useTMSContext();
  const { setValue, watch, formState, getValues } = formMethods;
  const fieldAttrs = useFieldAttributes();
  const stopPrefix = stop;
  const isPickup = stop === 'pickup';

  const isRoarTenant =
    tmsName === TMS.QuantumEdge && tenant?.toLowerCase().includes('roar');

  const stops = getValues('stops') || [];
  const calculatedStopIndex = isPickup
    ? stops.findIndex((s) => s.stopType === LoadStopsItemTypes.Pickup)
    : stops.findIndex(
        (s) =>
          s.stopType === LoadStopsItemTypes.Delivery ||
          s.stopType === LoadStopsItemTypes.Dropoff
      );

  const stopIndex =
    providedStopIndex !== undefined ? providedStopIndex : calculatedStopIndex;
  const refNumberAttr = getFieldAttribute(
    fieldAttrs,
    `stops.${stopIndex}.refNumber`
  );

  const watchedApptStartTime = watch(
    `stops.${stopIndex}.apptStartTime` as FieldPath<NormalizedLoad>
  );

  const prevApptStartTimeRef = useRef(watchedApptStartTime);
  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);
  const [openNestedSections, setOpenNestedSections] = useState<string[]>(() => {
    if (!autoExpandAccordions) {
      return [];
    }
    if (stopIndex === -1) {
      return [LoadStopNestedSection.Address];
    }
    return [LoadStopNestedSection.Address, LoadStopNestedSection.Appointment];
  });

  useEffect(() => {
    if (
      stopIndex === -1 ||
      !suggestionTouchesStopDetails(clickedSuggestion, stop)
    ) {
      return;
    }
    setOpenNestedSections((prev) => {
      if (prev.includes(LoadStopNestedSection.Appointment)) {
        return prev;
      }
      return [...prev, LoadStopNestedSection.Appointment];
    });
  }, [clickedSuggestion?.id, stop, stopIndex]);

  useEffect(() => {
    const isApptStartTimeDirty =
      formState.dirtyFields.stops?.[stopIndex]?.apptStartTime;
    const isApptEndTimeDirty =
      formState.dirtyFields.stops?.[stopIndex]?.apptEndTime;

    if (
      isRoarTenant &&
      stopIndex !== -1 &&
      (isApptStartTimeDirty || isApptEndTimeDirty) && // isApptEndTimeDirty ensures if user resets the appt start time to original value, set the appt end time to the same value
      watchedApptStartTime &&
      prevApptStartTimeRef.current !== watchedApptStartTime
    ) {
      setValue(
        `stops.${stopIndex}.apptEndTime` as FieldPath<NormalizedLoad>,
        watchedApptStartTime,
        { shouldValidate: true, shouldDirty: true }
      );
    }

    prevApptStartTimeRef.current = watchedApptStartTime;
  }, [
    watchedApptStartTime,
    isRoarTenant,
    setValue,
    stopIndex,
    formState.dirtyFields,
  ]);

  return (
    <Flex direction='col' className='gap-4'>
      <Accordion
        type='multiple'
        className='w-full'
        value={openNestedSections}
        onValueChange={setOpenNestedSections}
      >
        <AccordionItem value={LoadStopNestedSection.Address}>
          <AccordionTrigger className='text-sm font-medium py-2'>
            Address & Contact Info
          </AccordionTrigger>
          <AccordionContent className='pt-2'>
            <Flex direction='col' gap='md'>
              <LoadBuildingTextInput
                name={`${stopPrefix}.name` as FieldPath<NormalizedLoad>}
                label='Name'
                readOnly={!isCreateMode}
                required={isCreateMode}
              />

              <LoadBuildingTextInput
                name={`${stopPrefix}.addressLine1` as FieldPath<NormalizedLoad>}
                label='Address Line 1'
                readOnly={!isCreateMode}
              />

              <LoadBuildingTextInput
                name={`${stopPrefix}.city` as FieldPath<NormalizedLoad>}
                label='City'
                readOnly={!isCreateMode}
              />

              <Flex direction='row' className='gap-4'>
                <Flex grow='1'>
                  <LoadBuildingTextInput
                    name={`${stopPrefix}.state` as FieldPath<NormalizedLoad>}
                    label='State'
                    readOnly={!isCreateMode}
                  />
                </Flex>
                <Flex grow='1'>
                  <LoadBuildingTextInput
                    name={`${stopPrefix}.zipCode` as FieldPath<NormalizedLoad>}
                    label='ZIP Code'
                    readOnly={!isCreateMode}
                    placeholder='12345'
                  />
                </Flex>
              </Flex>

              <LoadBuildingTextInput
                name={`${stopPrefix}.contact` as FieldPath<NormalizedLoad>}
                label='Contact'
                readOnly={!isCreateMode}
              />

              <LoadBuildingTextInput
                name={`${stopPrefix}.phone` as FieldPath<NormalizedLoad>}
                label='Phone'
                readOnly={!isCreateMode}
              />

              <LoadBuildingTextInput
                name={`${stopPrefix}.email` as FieldPath<NormalizedLoad>}
                label='Email'
                readOnly={!isCreateMode}
              />
            </Flex>
          </AccordionContent>
        </AccordionItem>

        {stopIndex !== -1 && (
          <AccordionItem
            value={LoadStopNestedSection.Appointment}
            className='border-b-0'
          >
            <AccordionTrigger className='text-sm font-medium py-2'>
              Appointment Info
            </AccordionTrigger>
            <AccordionContent className='pt-2'>
              <Flex direction='col' gap='md'>
                {!refNumberAttr?.isNotSupported && (
                  <LoadRefPillInput
                    name={`stops.${stopIndex}.refNumber`}
                    control={formMethods.control}
                    label='Ref #'
                    disabled={Boolean(refNumberAttr?.isReadOnly)}
                    originalSuggestionData={
                      originalSuggestionData || formMethods.getValues()
                    }
                  />
                )}

                <div className='w-full [&>div]:w-full'>
                  <LoadSelectInput
                    name={`stops.${stopIndex}.apptType`}
                    label='Appointment Type'
                    options={quantumEdgeApptTypes}
                    placeholder='Select type'
                  />
                </div>

                <Controller
                  name={`stops.${stopIndex}.apptRequired`}
                  control={formMethods.control}
                  render={({ field }) => (
                    <Flex align='center' gap='sm'>
                      <Checkbox
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                        }}
                        checked={field.value || undefined}
                      />
                      <Label
                        htmlFor={`stops.${stopIndex}.apptRequired`}
                        name=''
                      >
                        Appointment Required?
                      </Label>
                    </Flex>
                  )}
                />

                <LoadDateTimeInput
                  name={`stops.${stopIndex}.readyTime`}
                  label='Req From'
                  options={datetimeFieldOptions}
                  load={formMethods.watch()}
                />

                <LoadDateTimeInput
                  name={`stops.${stopIndex}.readyEndTime`}
                  label='Req To'
                  options={datetimeFieldOptions}
                  load={formMethods.watch()}
                />

                <LoadDateTimeInput
                  name={`stops.${stopIndex}.apptStartTime`}
                  label='Appointment Start Time'
                  options={datetimeFieldOptions}
                  load={formMethods.watch()}
                />

                <LoadDateTimeInput
                  name={`stops.${stopIndex}.apptEndTime`}
                  label='Appointment End Time'
                  options={datetimeFieldOptions}
                  load={formMethods.watch()}
                />

                <LoadTextInput
                  name={`stops.${stopIndex}.apptNote`}
                  label='Appointment Note'
                  readOnly={false}
                />
              </Flex>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </Flex>
  );
}
